import { importLibrary, setOptions } from "@googlemaps/js-api-loader";

let placesLibraryPromise: Promise<void> | null = null;
const LOAD_TIMEOUT_MS = 10_000;

export function loadGoogleMapsPlacesApi(): Promise<void> {
  if (typeof window === "undefined") {
    return Promise.reject(
      new Error("Google Maps Places API can only be loaded in the browser."),
    );
  }

  if (window.google?.maps?.places) {
    return Promise.resolve();
  }

  if (placesLibraryPromise) {
    return placesLibraryPromise;
  }

  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY?.trim();

  if (!apiKey) {
    return Promise.reject(
      new Error("Missing VITE_GOOGLE_MAPS_API_KEY for Places autocomplete."),
    );
  }

  setOptions({
    key: apiKey,
    v: "weekly",
    libraries: ["places"],
  });

  let timeoutId: ReturnType<typeof setTimeout>;
  const timeoutPromise = new Promise<void>((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(
        new Error(
          `Google Maps Places library load timed out after ${LOAD_TIMEOUT_MS}ms.`,
        ),
      );
    }, LOAD_TIMEOUT_MS);
  });

  placesLibraryPromise = Promise.race([
    importLibrary("places").then(() => undefined),
    timeoutPromise,
  ])
    .then(() => undefined)
    .catch((error: unknown) => {
      placesLibraryPromise = null;
      throw new Error(
        `Unable to load the Google Maps Places library: ${
          error instanceof Error ? error.message : "unknown error"
        }`,
      );
    })
    .finally(() => {
      clearTimeout(timeoutId);
    });

  return (
    placesLibraryPromise ?? Promise.reject(new Error("Loader init failed."))
  );
}
